import { checkResponseStatus } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get token - avoids circular dependency
const getAuthToken = () => {
    // Try to get from localStorage first (fallback)
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

// Get notification count
export async function getNotificationCount() {
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    // If no token, return a default response to avoid errors
    if (!accessToken) {
        console.warn('No authentication token found for notification count');
        return { data: { count: 0 } };
    }

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'Cookie': `jwt=${accessToken}`,
    };

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/notification/count`, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch notification count';
        try {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
        } catch (e) {
            const errorText = await response.text();
            console.error('Non-JSON response:', errorText);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

// Get new notifications
export async function getNewNotifications(page = 1, limit = 16) {
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    // If no token, return empty result
    if (!accessToken) {
        console.warn('No authentication token found for new notifications');
        return { data: { data: [], total: 0, page: 1, limit: 16, results: 0, last_page: 1 } };
    }

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'Cookie': `jwt=${accessToken}`,
    };

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/notification/new?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch new notifications';
        try {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
        } catch (e) {
            const errorText = await response.text();
            console.error('Non-JSON response:', errorText);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

// Get old notifications
export async function getOldNotifications(page = 1, limit = 16) {
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    // If no token, return empty result
    if (!accessToken) {
        console.warn('No authentication token found for old notifications');
        return { data: { data: [], total: 0, page: 1, limit: 16, results: 0, last_page: 1 } };
    }

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'Cookie': `jwt=${accessToken}`,
    };

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/notification/old?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch old notifications';
        try {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
        } catch (e) {
            const errorText = await response.text();
            console.error('Non-JSON response:', errorText);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

// Mark all notifications as read
export async function markAllAsRead() {
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    // If no token, throw error since this is a write operation
    if (!accessToken) {
        throw new Error('Authentication token required to mark notifications as read');
    }

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'Cookie': `jwt=${accessToken}`,
    };

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/notification/read`, {
        method: 'PATCH',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to mark notifications as read';
        try {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
        } catch (e) {
            const errorText = await response.text();
            console.error('Non-JSON response:', errorText);
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }

    return response.json();
}
